import React, {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Loader, Modal } from 'ncoded-component-library';
import api from 'api';
import { PdfViewer } from 'components/PdfViewer';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { useTranslation } from 'react-i18next';
import {
  extractNodeWatermarkSettings,
  getBuyingPortfolio,
  getIsOwner,
  getPageUrl,
  getUserFullName,
  isDwg,
  isEml,
  isImage,
  isOfficeFileType,
  isPdf,
  isVideo,
} from 'utils';
import useFilePreviewTrackTime from 'hooks/useFilePreviewTrackTime';
import useDisableRightClick from 'hooks/useDisableRightClick';
import { SOFT_SIGNALS, SoftSignal } from 'models/Lead';
import SoftSignals from './components/SoftSignals';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { BUYING_PORTFOLIO_ROUTE } from 'data/global';
import { useNavigate } from 'react-router-dom';
import useMixpanelDocumentEvents from 'hooks/useMixpanelDocumentEvents';
import OfficeFilePreview from 'components/OfficeFilePreview';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { DataRoomFileBufferPreview } from 'models/File';
import useStartDownloadingFile from 'hooks/useStartDownloadingFile';
import useFileBufferPreviewQuery from 'hooks/useFileBufferPreviewQuery';
import useFileContentQuery from 'hooks/useFileContentQuery';
import useObjectUrl from 'hooks/useObjectUrl';
import CannotPreviewInBrowser from 'components/CannotPreviewInBrowser';
import AutodeskViewer from 'components/AutodeskViewer';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { NeedToSignNdaModalRef } from 'components/NeedToSignNdaModal/NeedToSignNdaModal.component';
import NeedToSignNdaModal from 'components/NeedToSignNdaModal';
import ShareableLinkNdaProcessing from 'router/subrouters/ShareableLink/components/ShareableLinkNdaProcessing';
import ShareableLinkNdaCountersign from 'router/subrouters/ShareableLink/components/ShareableLinkNdaCountersign';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import { SoftSignalSeenEventProps } from 'models/MixpanelEvents';
import { isMaximumSelected } from 'router/subrouters/DataRoom/utils';
import LockedFilePreview from './components/LockedFilePreview';
import usePreviewModalNavigation from 'hooks/usePreviewModalNavigation';
import PreviewModalNavigationButtons from 'components/PreviewModalNavigationButtons';
import PreviewModalDownloadButton from 'components/PreviewModalDownloadButton';
import useMatchMedia from 'hooks/useMatchMedia';

import '@react-pdf-viewer/core/lib/styles/index.css';
import './DataroomNodeViewModal.styles.scss';

const getCannotPreviewInBrowser = ({
  type,
  urn,
  isAutodeskProccessed,
}: DataRoomFileBufferPreview) =>
  isEml(type) || (isDwg(type) && (!urn || !isAutodeskProccessed));

export type SequenceItem = {
  isLastItem: boolean;
  // The lead's chat with the broker, where "I have a question" lands.
  listingEnquiryId: string;
  chatSessionId: string;
};

export type SoftSignalsDetails = {
  displayBanner?: boolean;
  lastSoftSignal?: SoftSignal;
  sequenceItem?: SequenceItem;
};

type DataroomNodeViewModalProps = {
  className?: string;
  node: DataRoomNode;
  isCurrentlyActiveNode?: boolean;
  dataRoomName?: string;
  ownerId: string;
  userSide: QAndASides;
  dealRoomId: string;
  onPrev?: () => void;
  onNext?: () => void;
};

const DataroomNodeViewModal: ForwardRefRenderFunction<
  ModalRef,
  DataroomNodeViewModalProps
> = (props, ref) => {
  const {
    className,
    node,
    dataRoomName,
    ownerId,
    userSide,
    dealRoomId,
    isCurrentlyActiveNode = true,
    onPrev,
    onNext,
  } = props;

  const {
    _id: nodeId,
    name: documentName,
    dataRoomId,
    canDownload,
    version,
    isBuyerFile,
    userId,
    fileSecurity,
  } = node || {};

  const [Fallback, setFallback] = useState<ReactNode>();
  const [cannotPreview, setCannotPreview] = useState(false);
  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [needToSignNdaModal, needToSignNdaModalRef] =
    useCallbackRef<NeedToSignNdaModalRef>();

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    currentUser: { _id: currentUserId, firstName, lastName, email },
  } = useContext(CurrentUserContext);

  const navigate = useNavigate();

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const classes = classNames(
    'preview-file-modal-template',
    'data-room-node-view-modal',
    className,
  );

  const isLocked = isMaximumSelected(fileSecurity);

  useImperativeHandle(ref, () => modal, [modal]);

  const { isModalOpen, canNavigate } = usePreviewModalNavigation({
    modal,
    onPrev,
    onNext,
  });

  const { query, removeFromCache, updateSoftSignal, markStale } =
    useFileBufferPreviewQuery({
      nodeId,
      isCurrentlyActiveNode,
      version,
      enabled:
        !!isModalOpen && !!nodeId && !Fallback && !cannotPreview && !isLocked,
    });

  const { data, isFetching } = query;

  const {
    url,
    type,
    autodeskToken,
    urn,
    displayBanner,
    lastSoftSignal,
    sequenceItem,
  } = data || {};

  // cache the actual file bytes for browser-rendered types so re-opening the same
  // file doesn't re-download it; office (MS viewer) and dwg (Autodesk) need the
  // signed url / urn and cannot be byte-cached client-side.
  const isBrowserRenderable = isPdf(type) || isImage(type) || isVideo(type);

  const {
    data: fileBlob,
    isFetching: isContentFetching,
    isError: isContentError,
  } = useFileContentQuery({
    id: nodeId,
    url,
    version,
    enabled: !!isModalOpen && isBrowserRenderable && !!url && !Fallback,
  });

  const objectUrl = useObjectUrl(fileBlob);

  // fall back to the signed url if byte caching fails (e.g. CORS) so the preview
  // still renders instead of showing nothing.
  const previewUrl = isBrowserRenderable
    ? objectUrl ?? (isContentError ? url : undefined)
    : url;
  const isPreviewFetching =
    isFetching ||
    (isBrowserRenderable &&
      !objectUrl &&
      !isContentError &&
      (isContentFetching || !!fileBlob));

  const userFullName = useMemo(
    () => getUserFullName(firstName, lastName),
    [firstName, lastName],
  );

  useDisableRightClick(isModalOpen);

  const startDownloadingFile = useStartDownloadingFile();

  const renderDownloadButton = useMemo(
    () => (
      <PreviewModalDownloadButton
        shouldRender={canDownload && !isLocked}
        resetKey={nodeId}
        onDownload={() => startDownloadingFile(nodeId)}
      />
    ),
    [canDownload, isLocked, nodeId, startDownloadingFile],
  );

  // react to the fetched preview: NDA / processing states are evicted from cache so
  // they always re-check the server, while previewable / cannot-preview responses stay
  // cached (handled by the query hook).
  useEffect(() => {
    if (!isModalOpen || !query.isSuccess) return;

    if (!data) {
      removeFromCache();
      modal?.close();
      return;
    }

    const {
      requiresNdaSign,
      shareableLinkId,
      isWaitingNdaConfirmation,
      isWaitingCounterSign,
    } = data;

    if (isWaitingNdaConfirmation) {
      removeFromCache();
      setFallback(<ShareableLinkNdaProcessing />);
      return;
    }

    if (isWaitingCounterSign) {
      removeFromCache();
      setFallback(<ShareableLinkNdaCountersign />);
      return;
    }

    if (requiresNdaSign) {
      removeFromCache();
      modal?.close();
      needToSignNdaModal.open(
        api.shareableLinks.getNDADocusignUrl(shareableLinkId, {
          redirectUrl: getPageUrl(),
        }),
      );
      return;
    }

    if (getCannotPreviewInBrowser(data)) {
      // a DWG here is still being processed by Autodesk (no urn yet / not processed) —
      // that's a transient state, so don't cache it. Otherwise re-opening keeps
      // serving the stale "cannot preview" and re-downloads instead of rendering in
      // the viewer once processing finishes.
      if (isDwg(data.type)) removeFromCache();

      setCannotPreview(true);
    }
  }, [
    isModalOpen,
    query.isSuccess,
    data,
    removeFromCache,
    modal,
    needToSignNdaModal,
  ]);

  useEffect(() => {
    if (!query.isError) return;

    popServerError(query.error);
    modal?.close();
  }, [query.isError, query.error, modal, popServerError]);

  const sendReport = useCallback(
    (time: number, currentPage: number) => {
      api.dataRoom.reports.trackTime(
        dataRoomId,
        nodeId,
        currentPage,
        time,
        version || 1,
      );
    },
    [dataRoomId, nodeId, version],
  );

  const getMixpanelProps = useGetMixpanelProps();

  const { onDocumentViewed } = useMixpanelDocumentEvents({
    documentName,
    dataRoomName,
    ownerId,
    userSide,
  });

  const { isLoaded, onDocumentLoad, onPageChange } = useFilePreviewTrackTime({
    isModalOpen,
    trackingKey: nodeId,
    sendReport,
    onDocumentViewed,
  });

  const onSoftSignalSuccess = useCallback(
    (lastSoftSignal: SoftSignal, withdrawInterest?: boolean) => {
      updateSoftSignal(lastSoftSignal);

      // A reaction moves the lead on, so a reopen should get the file's new bar.
      if (sequenceItem) markStale();

      if (withdrawInterest) {
        popSuccess(t('interestWithdrawnSuccessfully'));
        modal?.close();
        setTimeout(() => navigate(BUYING_PORTFOLIO_ROUTE), 2000);
        return;
      }

      // The question is posted to chat, so the lead continues there.
      if (sequenceItem && lastSoftSignal === SOFT_SIGNALS.UNSURE) {
        const { listingEnquiryId, chatSessionId } = sequenceItem;

        modal?.close();
        navigate(
          `${getBuyingPortfolio(
            listingEnquiryId,
            'messaging',
          )}/${chatSessionId}`,
        );
      }
    },
    [markStale, modal, navigate, popSuccess, sequenceItem, t, updateSoftSignal],
  );

  const renderFooter = useMemo(
    () =>
      isLoaded &&
      displayBanner &&
      !isBuyerFile &&
      !getIsOwner(userId, currentUserId),
    [displayBanner, isBuyerFile, isLoaded, userId, currentUserId],
  );

  useEffect(() => {
    if (!isModalOpen || !renderFooter || lastSoftSignal) return;

    mixpanelService.softSignalSeen(
      getMixpanelProps({
        dataRoomId,
        documentId: nodeId,
        dealId: dealRoomId,
      }) as SoftSignalSeenEventProps,
    );
  }, [
    isModalOpen,
    renderFooter,
    dataRoomId,
    nodeId,
    dealRoomId,
    lastSoftSignal,
    getMixpanelProps,
  ]);

  const RenderFooter = useMemo(
    () =>
      renderFooter && (
        <SoftSignals
          lastSoftSignal={lastSoftSignal}
          sequenceItem={sequenceItem}
          dataRoomNodeId={nodeId}
          dataRoomId={dataRoomId}
          dealRoomId={dealRoomId}
          onSuccess={onSoftSignalSuccess}
        />
      ),
    [
      renderFooter,
      lastSoftSignal,
      sequenceItem,
      dataRoomId,
      dealRoomId,
      nodeId,
      onSoftSignalSuccess,
    ],
  );

  // on close: reset the fallback, and for office/dwg drop the cached preview.
  // those hand a short-lived signed url/token to an external viewer that downloads
  // the file server-side, so a cached (expired) url breaks the next open with
  // "Download failed" — re-opening must fetch a fresh url. Browser-renderable types
  // stay cached (their bytes are byte-cached separately, so the url is never reused).
  useEffect(() => {
    if (isModalOpen) return;

    if (Fallback) setFallback(null);
    if (cannotPreview) setCannotPreview(false);

    if (data && (isOfficeFileType(type) || isDwg(type))) removeFromCache();
  }, [isModalOpen, Fallback, cannotPreview, data, type, removeFromCache]);

  // navigating to a sibling file changes nodeId; clear any fallback from the
  // previous file (cannot-preview / NDA / processing) so it doesn't carry over
  // onto the next one. The preview queries are keyed by nodeId and refetch on
  // their own.
  useEffect(() => {
    setFallback(null);
    setCannotPreview(false);
  }, [nodeId]);

  return (
    <>
      <Modal
        ref={modalRef}
        className={classes}
        lockScroll={false}
        title={
          cannotPreview || !renderDownloadButton ? (
            <span style={{ minHeight: isPhablet ? '34px' : '42px' }} />
          ) : (
            renderDownloadButton
          )
        }
        footer={RenderFooter}
      >
        {isPreviewFetching && <Loader />}

        {Fallback}

        {cannotPreview && (
          <CannotPreviewInBrowser
            title={`${t('thisFileTypeCantBePreviewedInTheBrowser')}.`}
            description={t('downloadTheFileToViewIt')}
            showDescription={!!renderDownloadButton}
            actions={renderDownloadButton}
          />
        )}

        <PreviewModalNavigationButtons
          canNavigate={canNavigate}
          onPrev={onPrev}
          onNext={onNext}
        />

        {isLocked ? (
          <LockedFilePreview
            nodeId={nodeId}
            documentName={documentName}
            dataRoomId={dataRoomId}
            dataRoomName={dataRoomName}
          />
        ) : (
          <>
            {isPdf(type) && (
              <PdfViewer
                url={previewUrl}
                onPageChange={onPageChange}
                onDocumentLoad={onDocumentLoad}
              />
            )}

            {isImage(type) && previewUrl && (
              <img
                alt={documentName}
                src={previewUrl}
                onLoad={() => onDocumentLoad()}
              />
            )}

            {isVideo(type) && (
              <video
                src={previewUrl}
                controls
                controlsList="nodownload"
                onLoadedData={() => onDocumentLoad()}
              />
            )}

            {isOfficeFileType(type) && (
              <OfficeFilePreview
                data={data}
                email={email}
                fullName={userFullName}
                watermarkSettings={extractNodeWatermarkSettings(node)}
                onDocumentLoad={onDocumentLoad}
              />
            )}

            {isDwg(type) && (
              <AutodeskViewer
                urn={urn}
                accessToken={autodeskToken}
                email={email}
                fullName={userFullName}
                ipAddress={data?.ipAddress}
                watermarkSettings={extractNodeWatermarkSettings(node)}
                onDocumentLoad={onDocumentLoad}
              />
            )}
          </>
        )}
      </Modal>

      <NeedToSignNdaModal
        ref={needToSignNdaModalRef}
        title={t('signNdaToAccessThisFile')}
      >
        <p>{t('thisFileRequiresSignedNDABeforeAccessIsGranted')}</p>
      </NeedToSignNdaModal>
    </>
  );
};

export default forwardRef(DataroomNodeViewModal);
